import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgScrollbar } from 'ngx-scrollbar';
import { Subscription } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import { UploadFileType } from 'src/app/model/Upload';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { CatalogProduct } from '../../model/CatalogProduct';
import { DataviewComponent } from '../controls/dataview/dataview.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { TreeEvent, TreeItem } from '../controls/tree/tree.component';
import { DragDropComponent, UploadModalData } from '../dialogs/drag-drop/drag-drop.component';
import { CatalogTreeProduct } from './../../model/CatalogProduct';
import { CatalogService } from './../../service/catalog.service';
import { DataService } from './../../service/data.service';
import { EventService } from './../../service/event.service';

@Component({
    selector: 'app-catalog',
    templateUrl: './catalog.component.html',
    styleUrls: ['./catalog.component.css'],
    standalone: false
})
export class CatalogComponent implements OnInit, AfterViewInit {

  @ViewChild(DataviewComponent) dataviewRef: DataviewComponent;

  treeItems: TreeItem[] = [];
  treeProducts: CatalogTreeProduct[];
  productFilterForm = new FormGroup({
    name: new FormControl('', [
    ])
  });
  title: string = '';
  solutionInfoSubscription: Subscription;
  subscription: Subscription;
  routerSubscription: Subscription;
  expandsubscription: Subscription;
  scrollbarRef: NgScrollbar;
  isSapMode: boolean = false;
  isLoading: boolean = true;
  isManifestUpdating: boolean = false;
  eventMessageSubscription: Subscription;

  constructor(public catalogSrv: CatalogService, public eventService: EventService, public router: Router,
    public dataSrv: DataService, public route: ActivatedRoute, public modal: Modal, public el: ElementRef) { }

  ngOnInit(): void {

    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.isManifestUpdating = newSolutionInfo.manifestStatus.isUpdating;
      this.isSapMode = newSolutionInfo.isSModeEnabled;

      if (this.routerSubscription == undefined) {
        this.initView();
      }
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if (msg.eventType == Events.ManifestUpdateStarted) {
        Utils.closeModals(this.eventService);
        this.isManifestUpdating = true;
      } else if (msg.eventType == Events.ManifestUpdateComplete) {
        this.isManifestUpdating = false;
      }
    });
  }

  initView(): void {
    this.subscription = this.dataSrv.getData().subscribe(prod => {
      this.loadWaitForTree(prod);
    });

    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects == "/catalog/overview") {
          this.unSelectAll();
        }
        this.setTitle(event.urlAfterRedirects);
      }
    });

    this.setTitle(this.router.url);

    this.getProducts();
  }

  ngAfterViewInit() {
    if (this.dataviewRef != undefined) {
      this.scrollbarRef = this.dataviewRef.sidePanelScrollRef;
    }
  }

  ngOnDestroy() {
    if (this.solutionInfoSubscription != undefined) {
      this.solutionInfoSubscription.unsubscribe();
    }
    if (this.routerSubscription != undefined) {
      this.routerSubscription.unsubscribe();
    }
    if (this.subscription != undefined) {
      this.subscription.unsubscribe();
    }
    if (this.eventMessageSubscription != undefined) {
      this.eventMessageSubscription.unsubscribe();
    }
  }

  get name() { return this.productFilterForm.get('name'); }

  setTitle(url: string): void {
    if (url == "/catalog/overview") {
      this.unSelectAll();
      this.title = 'All Environments - Latest releases';
    } else if (url.indexOf("/details") > -1 && url.indexOf("/download") == -1) {
      this.title = 'All Environments - Product details';
    } else if (url.indexOf("/download") > -1) {
      this.title = 'All Environments - Product download';
    }
  }

  getProducts(): void {
    if (this.treeItems.length == 0) {
      this.catalogSrv.getProducts().pipe(
        tap(resp => {
          this.treeProducts = resp.body;
          this.treeProducts.forEach((treeProd) => {
            this.treeItems.push(new TreeItem(this.isSapMode ? treeProd.sapName : treeProd.product_name, null, null, treeProd));
          });

        }),
        finalize(()=>{
          this.isLoading = false;
        })
      ).subscribe();
    }
  }

  loadWaitForTree(prod: any): void {
    if (this.treeProducts == undefined) {
      setTimeout(() => {
        this.loadWaitForTree(prod);
      }, 10);
    } else {
      this.treeItems.forEach((treeItem) => {
        if (treeItem.data.product_name == prod.productName) {
          this.getProductVersion(prod.productName, treeItem, true, prod.version, true);
          return;
        }
      });
    }
  }

  expandTree(treeEvent: TreeEvent) {
    switch (treeEvent.level) {
      case 0: {
        if (treeEvent.item.getChildren().length == 0) {
          this.getProductVersion(treeEvent.item.data.product_name, treeEvent.item, !treeEvent.item.expand, "", false);
        } else {
          treeEvent.item.expand = !treeEvent.item.expand;
        }
        break;
      }
      case 1: {
        this.unSelectAll();
        treeEvent.item.selected = true;
        this.goToCatalogDetails(treeEvent.item.data);
        break;
      }
      default: {
        break;
      }
    }
  }

  unSelectAll() {
    this.treeItems.forEach((product) => {
      product.getChildren().forEach((version) => {
        version.selected = false;
      });
    });
  }

  getProductVersion(name: string, treeItem: TreeItem, expand: boolean, selectedVersion: string = "", scroll: boolean) {
    var populateVersions = (treeItem.getChildren().length == 0);
    this.unSelectAll();
    if (populateVersions) {
      this.catalogSrv.getProductVersion(name).pipe(
        tap(resp => {
          var versions = resp.body;
          var tmpSelectedVersion;
          this.unSelectAll();
          versions.forEach((version) => {
            var tmpTreeItem = new TreeItem(this.isSapMode ? version.sapName : version.displayName, null, null, version, false);
            if (selectedVersion != "" && selectedVersion == version.version) {
              tmpTreeItem.selected = true;
              tmpSelectedVersion = version;
            }
            treeItem.addChild(tmpTreeItem);
          });
          treeItem.expand = expand;
          if (tmpSelectedVersion != undefined) {
            if (this.dataviewRef.sidePanelScrollRef != undefined && this.el != undefined) {
              setTimeout(() => {
                var element = this.el.nativeElement.querySelector("#treeCtrl [otdata=\"" + tmpSelectedVersion.displayName + "\"]");
                if (element != undefined) {
                  this.dataviewRef.sidePanelScrollRef.scrollTo({ top: element.offsetTop });
                }
              }, 10);
            }
          }
        })).subscribe();
    } else {
      this.unSelectAll();
      var tmpSelectedVersion;
      treeItem.getChildren().forEach(version => {
        if (selectedVersion != "" && selectedVersion == version.data.version) {
          version.selected = true;
          tmpSelectedVersion = version;
          return;
        }
      });
      if (tmpSelectedVersion != undefined) {
        if (this.dataviewRef.sidePanelScrollRef != undefined && this.el != undefined) {
          setTimeout(() => {
            var element = this.el.nativeElement.querySelector("#treeCtrl [otdata=\"" + tmpSelectedVersion.displayName + "\"]");
            if (element != undefined) {
              this.dataviewRef.sidePanelScrollRef.scrollTo({ top: element.offsetTop });
            }
          }, 10);
        }
      }
    }
  }

  getProductName(product: CatalogProduct, displayName: boolean = false): string {
    var name = ""
    if (this.isSapMode && product.sapName != undefined && product.sapName != "") {
      name = product.sapName;
    } else {
      name = displayName ? product.displayName : product.productName;
    }
    return name;
  }

  goToCatalogDetails(product: CatalogProduct): void {
    product.selected = true;
    this.router.navigate(['/catalog/details/', product.productName, product.product_uid]);
  }


  onClickDragDrop(): void {
    if(this.isManifestUpdating){
      const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Error`,
          type: ModalMessageType.Error,
          message: $localize`Files cannot be uploaded while the manifest is being updated, please try again later.`,
          primaryBtn: $localize`OK`,
          width: '585px'
        },
        id: 'otsc-file-upload-not-allowed-msg'
      }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        sub.unsubscribe();
      });
    } else {
      this.modal.open(DragDropComponent, {
        data: new UploadModalData(UploadFileType.PRODUCT, false),
        id: 'otsc-drag-drop-dialog',
        customclass: 'ot-modal-side-panel ot-modal-side-panel-md'
      });
    }
  }

  public showPage(): boolean {
    return !this.isManifestUpdating;
  }

  public reloadAfterManifestUpdate(): void {
    setTimeout(() => {
      window.location.reload();
    }, 250);
  }
}
